import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { DefaultButton, Link, Text } from "@fluentui/react";
import { strings } from "@src/i18n/strings";
import { friendlyErrorMessage } from "@taskpane/utils/friendlyErrorMessage";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { ChevronRight } from "lucide-react";
import type { Email, LoadStatus, OrganizationCustomerRow } from "@src/types/api";
import { findMatchedCustomerForSender } from "@shared/utils/orgCustomerMatch";
import { interactionLeadLocalPart } from "@shared/utils/interactionLeadDisplay";
import { shipmentUiBucketFromUnifiedDoc } from "@shared/utils/shipmentUiBucket";
import { useAllEmailsData } from "@taskpane/hooks/useAllEmailsData";
import { useStatuses } from "@taskpane/hooks/useStatuses";
import {
  CLASSIFICATION_IN_SCOPE,
  CLASSIFICATION_INSUFFICIENT,
  findStatus,
} from "@shared/status/statusResolver";
import { FALLBACK_STATUS } from "@shared/status/types";
import type { StatusDefinition } from "@shared/status/types";
import DynamicStatusIcon from "./dashboard/DynamicStatusIcon";
import StatusLoadList, { type StatusLoad } from "./dashboard/StatusLoadList";

const dashboardStrings = strings.components.shipmentDashboard;
const appStrings = strings.components.app;

// Ordered list of statuses to display on the dashboard
const DASHBOARD_STATUS_ORDER: LoadStatus[] = [
  "Processing",
  "Ready to Quote",
  "Quoted",
  "Quote Sent",
  "Ready to Book",
  "Load Won",
  "BOL Sent",
  "Rejected",
  "Failed",
];

// Fallback bucket label for shipments whose loadStatus is missing or not in DASHBOARD_STATUS_ORDER.
const UNKNOWN_STATUS = "Unknown" as LoadStatus;

interface StatusGroup {
  status: LoadStatus;
  loads: StatusLoad[];
  infoNeededCount: number;
  readyCount: number;
}

function normalizeLabel(s: string): string {
  return s.trim().toLowerCase();
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

export function filterEmailsForDashboard(
  emails: Email[],
  organizationCustomers: OrganizationCustomerRow[],
  filters: { userSenders: string[]; customers: string[]; modes: Array<"FTL" | "LTL"> }
): Email[] {
  const { userSenders, customers, modes } = filters;
  const userLocalParts = new Set(
    userSenders
      .map((s) => interactionLeadLocalPart(s)?.toLowerCase())
      .filter((s): s is string => Boolean(s))
  );
  const normalizedCustomers = new Set(customers.map((c) => normalizeLabel(c)));
  const selectedModes = new Set(modes);
  const hasUserFilter = userLocalParts.size > 0;
  const hasCustomerFilter = normalizedCustomers.size > 0;
  const hasModeFilter = selectedModes.size > 0;
  const hasAnyFilter = hasUserFilter || hasCustomerFilter || hasModeFilter;

  const shipmentTouchedByUser = (shipment: Email["shipments"][number]): boolean =>
    (shipment.statusHistory ?? []).some((entry) => {
      const raw = typeof entry.interactingUser === "string" ? entry.interactingUser.trim() : "";

      if (!raw) return false;
      const key = (interactionLeadLocalPart(raw) ?? raw).toLowerCase();

      return key.length > 0 && userLocalParts.has(key);
    });

  return emails
    .map((email) => ({ ...email, shipments: email.shipments ?? [] }))
    .filter((email) => {
      if (email.shipments.length === 0) return false;

      if (!hasAnyFilter) return true;

      const userMatch = hasUserFilter && email.shipments.some(shipmentTouchedByUser);

      let customerMatch = false;

      if (hasCustomerFilter) {
        const row = findMatchedCustomerForSender(organizationCustomers, email.sender);
        const c = typeof row?.customer === "string" ? row.customer.trim() : "";

        customerMatch = normalizedCustomers.has(normalizeLabel(c));
      }

      const modeMatch =
        hasModeFilter &&
        email.shipments.some((shipment) =>
          selectedModes.has(shipmentUiBucketFromUnifiedDoc(shipment))
        );

      return userMatch || customerMatch || modeMatch;
    })
    .map((email) => {
      const needsShipmentFilter = hasUserFilter || hasModeFilter;

      if (!needsShipmentFilter) return email;
      return {
        ...email,
        shipments: email.shipments.filter((shipment) => {
          const modeOk =
            !hasModeFilter || selectedModes.has(shipmentUiBucketFromUnifiedDoc(shipment));
          const userOk = !hasUserFilter || shipmentTouchedByUser(shipment);

          return modeOk && userOk;
        }),
      };
    })
    .filter((email) => email.shipments.length > 0);
}

function buildStatusGroups(emails: Email[]): StatusGroup[] {
  const map = new Map<LoadStatus, StatusLoad[]>();

  for (const status of DASHBOARD_STATUS_ORDER) {
    map.set(status, []);
  }
  const unknownLoads: StatusLoad[] = [];
  // Same shipment is attached to every email sharing its globalId — show each load once.
  const seenShipmentIds = new Set<string>();

  for (const email of emails) {
    for (const shipment of email.shipments ?? []) {
      const shipmentId =
        typeof shipment.shipmentId === "string" ? shipment.shipmentId.trim() : "";

      if (shipmentId) {
        if (seenShipmentIds.has(shipmentId)) continue;
        seenShipmentIds.add(shipmentId);
      }
      const status = (shipment as { loadStatus?: LoadStatus }).loadStatus;

      // Keep shipments with a missing or unrecognized status visible instead of dropping them.
      if (status && map.has(status)) {
        map.get(status)!.push({ email, shipment });
      } else {
        unknownLoads.push({ email, shipment });
      }
    }
  }

  const RTQ = "Ready to Quote" as const;
  const RTB = "Ready to Book" as const;

  const groups = DASHBOARD_STATUS_ORDER.map((status) => {
    const loads = map.get(status) ?? [];
    let infoNeededCount = 0;
    let readyCount = 0;

    if (status === RTQ) {
      for (const { shipment } of loads) {
        const c = shipment.classification;

        if (c === CLASSIFICATION_INSUFFICIENT) infoNeededCount++;
        else if (c === CLASSIFICATION_IN_SCOPE) readyCount++;
      }
    } else if (status === RTB) {
      for (const { shipment } of loads) {
        const c = shipment.bolClassification;

        if (c === CLASSIFICATION_INSUFFICIENT) infoNeededCount++;
        else if (c === CLASSIFICATION_IN_SCOPE) readyCount++;
      }
    }

    return { status, loads, infoNeededCount, readyCount };
  }).filter((g) => g.loads.length > 0);

  if (unknownLoads.length > 0) {
    groups.push({
      status: UNKNOWN_STATUS,
      loads: unknownLoads,
      infoNeededCount: 0,
      readyCount: 0,
    });
  }
  return groups;
}

function definitionForDashboardRow(
  statuses: StatusDefinition[],
  status: LoadStatus
): StatusDefinition {
  return (
    findStatus(statuses, status) ?? {
      ...FALLBACK_STATUS,
      status,
      statusCode: status,
    }
  );
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    background: "#f5f5f5",
  },
  header: {
    padding: "12px 14px 8px",
    background: "#fff",
    borderBottom: "1px solid #e8e8e8",
  },
  headerTitle: {
    fontSize: "11px",
    fontWeight: "700" as const,
    color: "#888",
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  headerSubtitle: {
    fontSize: "13px",
    fontWeight: "600" as const,
    color: "#1a1a1a",
    marginTop: "1px",
  },
  body: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "12px",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: "12px",
    cursor: "pointer",
    gap: "12px",
    marginBottom: "10px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
  } as React.CSSProperties,
  statusLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  statusLabel: {
    fontSize: "14px",
    fontWeight: "500" as const,
    color: "#1a1a1a",
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "22px",
    height: "22px",
    borderRadius: "11px",
    background: "#f0f0f0",
    fontSize: "12px",
    fontWeight: "600" as const,
    color: "#555",
    padding: "0 6px",
    flexShrink: 0,
  } as React.CSSProperties,
  chevron: {
    color: "#bbb",
    flexShrink: 0,
  } as React.CSSProperties,
  rowRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
  } as React.CSSProperties,
  subcounts: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-end",
    gap: "3px",
  } as React.CSSProperties,
  subcountItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "11px",
    fontWeight: "500" as const,
    color: "#555",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  subcountDot: (color: string) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "120px",
  } as React.CSSProperties,
  errorText: {
    fontSize: "13px",
    color: "#c0392b",
    padding: "16px",
  },
  emptyText: {
    fontSize: "13px",
    color: "#aaa",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: "1px solid #eee",
  } as React.CSSProperties,
  filterControl: {
    minWidth: "84px",
    flex: "1 1 84px",
    "& .MuiInputBase-root": {
      height: "36px",
      borderRadius: "8px",
      fontSize: "13px",
      background: "#fff",
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
      top: "-2px",
    },
    "& .MuiInputLabel-shrink": {
      top: 0,
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: "8px",
      paddingBottom: "8px",
    },
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
      overflow: "hidden",
    },
    "& .MuiAutocomplete-input": {
      minWidth: "0 !important",
    },
  },
  selectedCountBubble: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    borderRadius: "9px",
    background: "#ececec",
    color: "#666",
    fontSize: "11px",
    fontWeight: 600,
    marginRight: "6px",
    padding: "0 4px",
    flexShrink: 0,
  } as React.CSSProperties,
};

/** Widen searchable filter menus so long emails / customer names are not clipped. */
const FILTER_AUTOCOMPLETE_COMPONENTS = {
  popper: {
    sx: {
      width: "max-content",
      maxWidth: "min(96vw, 720px)",
    },
  },
  paper: {
    sx: {
      width: "max-content",
      maxWidth: "min(96vw, 720px)",
      boxSizing: "border-box",
    },
  },
} as const;

const CUSTOMER_FILTER_AUTOCOMPLETE_COMPONENTS = {
  popper: {
    placement: "bottom" as const,
    sx: {
      width: "max-content",
      maxWidth: "min(96vw, 720px)",
    },
  },
  paper: {
    sx: {
      width: "max-content",
      maxWidth: "min(96vw, 720px)",
      boxSizing: "border-box",
    },
  },
} as const;

const FILTER_AUTOCOMPLETE_LISTBOX = {
  sx: {
    maxHeight: 280,
    boxSizing: "border-box",
    fontSize: "13px",
    "& .MuiAutocomplete-option": {
      whiteSpace: "nowrap",
      fontSize: "13px",
      minHeight: "32px",
      padding: "4px 8px",
    },
  },
} as const;

const SUBCOUNT_INFO_COLOR = "#f0a500";
const SUBCOUNT_READY_COLOR = "#27ae60";
const MODE_OPTIONS: Array<"FTL" | "LTL"> = ["FTL", "LTL"];
const FILTER_CHECKBOX_SX = {
  mr: 0.75,
  p: 0.25,
  "& .MuiSvgIcon-root": { fontSize: "14px" },
} as const;
const DASHBOARD_FILTERS_STORAGE_KEY = "shipmentDashboard.filters.v1";

export interface ShipmentDashboardProps {
  refreshToken?: number;
}

export default function ShipmentDashboard({ refreshToken = 0 }: ShipmentDashboardProps) {
  const { state, refresh } = useAllEmailsData(refreshToken);
  const { statuses } = useStatuses();
  const [selectedStatus, setSelectedStatus] = useState<LoadStatus | null>(null);
  const [userSenderFilter, setUserSenderFilter] = useState<string[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string[]>([]);
  const [modeFilter, setModeFilter] = useState<Array<"FTL" | "LTL">>([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  const organizationCustomers =
    state.status === "ok" ? state.organizationCustomers : ([] as OrganizationCustomerRow[]);

  const internalUsers = state.status === "ok" ? state.internalUsers : [];

  const senderOptions = useMemo(() => uniqueSorted(internalUsers), [internalUsers]);

  const customerOptions = useMemo(
    () =>
      uniqueSorted(
        organizationCustomers
          .map((c) => (typeof c.customer === "string" ? c.customer : ""))
          .filter(Boolean)
      ),
    [organizationCustomers]
  );

  const filteredEmails = useMemo(() => {
    if (state.status !== "ok") return [];
    return filterEmailsForDashboard(state.emails, organizationCustomers, {
      userSenders: userSenderFilter,
      customers: customerFilter,
      modes: modeFilter,
    });
  }, [state, organizationCustomers, userSenderFilter, customerFilter, modeFilter]);

  const groups = useMemo(() => buildStatusGroups(filteredEmails), [filteredEmails]);

  const totalLoads = useMemo(() => groups.reduce((sum, g) => sum + g.loads.length, 0), [groups]);

  const hasActiveFilters =
    userSenderFilter.length > 0 || customerFilter.length > 0 || modeFilter.length > 0;

  const clearFilters = useCallback(() => {
    setUserSenderFilter([]);
    setCustomerFilter([]);
    setModeFilter([]);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
      setFiltersHydrated(true);
      return;
    }

    try {
      const raw = window.localStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY);

      if (!raw) {
        setFiltersHydrated(true);
        return;
      }

      const parsed = JSON.parse(raw) as {
        userSenders?: unknown;
        customers?: unknown;
        modes?: unknown;
      };

      const persistedUsers = Array.isArray(parsed.userSenders)
        ? parsed.userSenders.filter((v): v is string => typeof v === "string")
        : [];
      const persistedCustomers = Array.isArray(parsed.customers)
        ? parsed.customers.filter((v): v is string => typeof v === "string")
        : [];
      const persistedModes = Array.isArray(parsed.modes)
        ? parsed.modes.filter((v): v is "FTL" | "LTL" => v === "FTL" || v === "LTL")
        : [];

      setUserSenderFilter(persistedUsers);
      setCustomerFilter(persistedCustomers);
      setModeFilter(persistedModes);
    } catch {
      // Ignore invalid persisted state and fall back to empty filters.
    } finally {
      setFiltersHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (
      !filtersHydrated ||
      typeof window === "undefined" ||
      typeof window.localStorage === "undefined"
    ) {
      return;
    }

    try {
      window.localStorage.setItem(
        DASHBOARD_FILTERS_STORAGE_KEY,
        JSON.stringify({
          userSenders: userSenderFilter,
          customers: customerFilter,
          modes: modeFilter,
        })
      );
    } catch {
      // Best effort persistence only.
    }
  }, [filtersHydrated, userSenderFilter, customerFilter, modeFilter]);

  const userFilterOptionLabel = (email: string) => interactionLeadLocalPart(email) ?? email;

  const renderFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
    selected: boolean
  ) => (
    <li {...props}>
      <Checkbox size="small" sx={FILTER_CHECKBOX_SX} checked={selected} />
      {option}
    </li>
  );

  const renderUserFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
    selected: boolean
  ) => (
    <li {...props}>
      <Checkbox size="small" sx={FILTER_CHECKBOX_SX} checked={selected} />
      {userFilterOptionLabel(option)}
    </li>
  );

  const renderFilterInput = (
    params: React.ComponentProps<typeof TextField>,
    label: string,
    emptyPlaceholder: string,
    selectedCount: number
  ) => (
    <TextField
      {...params}
      label={label}
      placeholder={selectedCount === 0 ? emptyPlaceholder : ""}
      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <>
            {selectedCount > 0 && <span style={styles.selectedCountBubble}>{selectedCount}</span>}
            {params.InputProps?.startAdornment}
          </>
        ),
      }}
    />
  );

  if (selectedStatus !== null) {
    const group = groups.find((g) => g.status === selectedStatus);

    return (
      <StatusLoadList
        status={selectedStatus}
        loads={group?.loads ?? []}
        statuses={statuses}
        onBack={() => setSelectedStatus(null)}
        senderOptions={senderOptions}
        customerOptions={customerOptions}
        userSenderFilter={userSenderFilter}
        setUserSenderFilter={setUserSenderFilter}
        customerFilter={customerFilter}
        setCustomerFilter={setCustomerFilter}
        modeFilter={modeFilter}
        setModeFilter={setModeFilter}
      />
    );
  }

  return (
    <Box sx={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>{dashboardStrings.eyebrow}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={styles.headerSubtitle}>{dashboardStrings.title}</div>
          {state.status === "ok" && <span style={styles.countBadge}>{totalLoads}</span>}
        </div>
        {state.status === "ok" && (
          <div style={styles.filtersRow}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              sx={styles.filterControl}
              options={senderOptions}
              value={userSenderFilter}
              onChange={(_, newValue) => {
                setUserSenderFilter(newValue);
              }}
              getOptionLabel={userFilterOptionLabel}
              isOptionEqualToValue={(a, b) => a === b}
              componentsProps={FILTER_AUTOCOMPLETE_COMPONENTS}
              ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
              renderTags={() => null}
              renderOption={(props, option, { selected }) =>
                renderUserFilterOption(props, option, selected)
              }
              renderInput={(params) =>
                renderFilterInput(
                  params,
                  dashboardStrings.filters.user,
                  dashboardStrings.filters.allUsers,
                  userSenderFilter.length
                )
              }
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              sx={styles.filterControl}
              options={customerOptions}
              value={customerFilter}
              onChange={(_, newValue) => {
                setCustomerFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(a, b) => a === b}
              componentsProps={CUSTOMER_FILTER_AUTOCOMPLETE_COMPONENTS}
              ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
              renderTags={() => null}
              renderOption={(props, option, { selected }) =>
                renderFilterOption(props, option, selected)
              }
              renderInput={(params) =>
                renderFilterInput(
                  params,
                  dashboardStrings.filters.customer,
                  dashboardStrings.filters.allCustomers,
                  customerFilter.length
                )
              }
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              sx={styles.filterControl}
              options={MODE_OPTIONS}
              value={modeFilter}
              onChange={(_, newValue) => {
                setModeFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(a, b) => a === b}
              componentsProps={FILTER_AUTOCOMPLETE_COMPONENTS}
              ListboxProps={FILTER_AUTOCOMPLETE_LISTBOX}
              renderTags={() => null}
              renderOption={(props, option, { selected }) =>
                renderFilterOption(props, option, selected)
              }
              renderInput={(params) =>
                renderFilterInput(
                  params,
                  dashboardStrings.filters.mode,
                  dashboardStrings.filters.allModes,
                  modeFilter.length
                )
              }
            />
          </div>
        )}
      </div>

      <div style={styles.body}>
        {state.status === "loading" && (
          <div style={styles.center}>
            <Spinner size={SpinnerSize.medium} label={dashboardStrings.loading} />
          </div>
        )}

        {state.status === "error" && (
          <div style={styles.errorText}>
            <Text>{friendlyErrorMessage(state.message)}</Text>
            <Box sx={{ marginTop: "8px" }}>
              <DefaultButton text={appStrings.error.tryAgain} onClick={refresh} />
            </Box>
          </div>
        )}

        {state.status === "ok" && groups.length === 0 && (
          <div style={{ ...styles.center, flexDirection: "column", gap: "8px" }}>
            <Text styles={{ root: styles.emptyText }}>
              {hasActiveFilters
                ? `${dashboardStrings.empty} ${dashboardStrings.clearFiltersHint}`
                : dashboardStrings.empty}
            </Text>
            {hasActiveFilters && (
              <Link onClick={clearFilters}>{dashboardStrings.clearFilters}</Link>
            )}
          </div>
        )}

        {state.status === "ok" &&
          groups.map(({ status, loads, infoNeededCount, readyCount }) => {
            const def = definitionForDashboardRow(statuses, status);
            const showSubcounts =
              (status === "Ready to Quote" || status === "Ready to Book") &&
              (infoNeededCount > 0 || readyCount > 0);

            return (
              <div
                key={status}
                style={styles.statusRow}
                onClick={() => setSelectedStatus(status)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedStatus(status);
                }}
              >
                <div style={styles.statusLeft}>
                  <DynamicStatusIcon
                    iconName={def.icon}
                    backgroundColor={def.color}
                    borderColor={def.borderColor}
                  />
                  <Text styles={{ root: styles.statusLabel }}>{status}</Text>
                </div>
                {showSubcounts && (
                  <div style={styles.subcounts}>
                    {infoNeededCount > 0 && (
                      <span style={styles.subcountItem}>
                        <span style={styles.subcountDot(SUBCOUNT_INFO_COLOR)} />
                        {infoNeededCount}
                      </span>
                    )}
                    {readyCount > 0 && (
                      <span style={styles.subcountItem}>
                        <span style={styles.subcountDot(SUBCOUNT_READY_COLOR)} />
                        {readyCount}
                      </span>
                    )}
                  </div>
                )}
                <div style={styles.rowRight}>
                  <span style={styles.countBadge}>{loads.length}</span>
                  <ChevronRight size={16} style={styles.chevron} />
                </div>
              </div>
            );
          })}
      </div>
    </Box>
  );
}
